import type { ComponentWithSource } from "../api/componentsData";
import { buildCollectionTableKey } from "../utils/collectionPaths";
import { getAssignableFields, getDefaultAssignFieldName } from "./inboxFieldUtils";

const STORAGE_PREFIX = "labwick-inbox-assign-prefs:";

export type InboxAssignPrefs = {
  lastCollectionKey: string;
  fieldsByCollection: Record<string, string>;
};

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

export function collectionPrefKey(component: {
  slug?: string;
  organization?: string | null;
}): string {
  return buildCollectionTableKey(component.slug, component.organization);
}

export function loadInboxAssignPrefs(userId: string | null | undefined): InboxAssignPrefs | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<InboxAssignPrefs>;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      lastCollectionKey:
        typeof parsed.lastCollectionKey === "string" ? parsed.lastCollectionKey : "",
      fieldsByCollection:
        parsed.fieldsByCollection && typeof parsed.fieldsByCollection === "object"
          ? { ...parsed.fieldsByCollection }
          : {},
    };
  } catch {
    return null;
  }
}

function saveInboxAssignPrefs(userId: string, prefs: InboxAssignPrefs): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
  } catch {
    // Ignore quota / private mode errors
  }
}

function mergePrefs(userId: string, patch: Partial<InboxAssignPrefs>): InboxAssignPrefs {
  const current = loadInboxAssignPrefs(userId) ?? {
    lastCollectionKey: "",
    fieldsByCollection: {},
  };
  const next: InboxAssignPrefs = {
    lastCollectionKey:
      patch.lastCollectionKey !== undefined
        ? patch.lastCollectionKey
        : current.lastCollectionKey,
    fieldsByCollection: {
      ...current.fieldsByCollection,
      ...(patch.fieldsByCollection ?? {}),
    },
  };
  saveInboxAssignPrefs(userId, next);
  return next;
}

export function rememberInboxCollection(
  userId: string | null | undefined,
  component: ComponentWithSource | null
): void {
  if (!userId || !component?.slug) return;
  mergePrefs(userId, { lastCollectionKey: collectionPrefKey(component) });
}

export function rememberInboxField(
  userId: string | null | undefined,
  component: ComponentWithSource | null,
  fieldName: string
): void {
  if (!userId || !component?.slug || !fieldName) return;
  const key = collectionPrefKey(component);
  mergePrefs(userId, {
    lastCollectionKey: key,
    fieldsByCollection: { [key]: fieldName },
  });
}

export function findCollectionByPrefKey(
  collections: ComponentWithSource[],
  collectionKey: string
): ComponentWithSource | null {
  if (!collectionKey) return null;
  return (
    collections.find((component) => collectionPrefKey(component) === collectionKey) ?? null
  );
}

export function resolveRememberedFieldName(
  component: ComponentWithSource | null,
  prefs: InboxAssignPrefs | null
): string {
  if (!component) return "";
  const key = collectionPrefKey(component);
  const remembered = prefs?.fieldsByCollection?.[key];
  const assignable = getAssignableFields(component);
  if (
    remembered &&
    assignable.some((field: { name?: string }) => field.name === remembered)
  ) {
    return remembered;
  }
  return getDefaultAssignFieldName(component);
}

export function resolveInitialInboxSelection(
  collections: ComponentWithSource[],
  userId: string | null | undefined
): {
  component: ComponentWithSource | null;
  fieldName: string;
} {
  const prefs = loadInboxAssignPrefs(userId);
  if (!prefs?.lastCollectionKey) {
    return { component: null, fieldName: "" };
  }
  const component = findCollectionByPrefKey(collections, prefs.lastCollectionKey);
  if (!component) {
    return { component: null, fieldName: "" };
  }
  return {
    component,
    fieldName: resolveRememberedFieldName(component, prefs),
  };
}
